// Loot-Helpers: Item-Level, Stat-Skalierung & Power-Score.
//
// Design:
// - Drops bekommen ein Level abhängig von der Zone (dreieckige Verteilung
//   Zone-2..Zone+2, Mittelpunkt = Zone, Mindestlevel 1, Boss-Bonus +1).
// - Stats skalieren mit `rarityMult × levelMult`. Bei gleichem Level gewinnt
//   die höhere Rarität, mit genug Level-Vorsprung schlägt eine Common-Waffe
//   eine Legendary niedrigerer Stufe.
// - `power` = einfache Summe der numerischen Stats × 10 (server-sortierfähig).

export type Rarity = "common" | "rare" | "epic" | "legendary";

export const RARITY_MULT: Record<Rarity, number> = {
  common: 1.0,
  rare: 1.4,
  epic: 1.9,
  legendary: 2.6,
};

export function levelMult(level: number): number {
  return 1 + Math.max(0, level - 1) * 0.35;
}

export function statScale(rarity: Rarity, level: number): number {
  return RARITY_MULT[rarity] * levelMult(level);
}

export function scaleStats(
  base: Record<string, number>,
  rarity: Rarity,
  level: number,
): Record<string, number> {
  const m = statScale(rarity, level);
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(base)) {
    const scaled = v * m;
    out[k] = Math.max(1, Math.round(scaled));
  }
  return out;
}

export function powerOf(stats: Record<string, number>): number {
  let sum = 0;
  for (const v of Object.values(stats)) {
    if (typeof v === "number" && Number.isFinite(v)) sum += v;
  }
  return Math.round(sum * 10);
}

// Drop-Raritäten je Tier – Epic/Legendary werden seltener als bisher.
type WeightedRarity = ReadonlyArray<readonly [Rarity, number]>;
const RARITY_WEIGHTS: Record<"normal" | "elite" | "boss", WeightedRarity> = {
  normal: [["common", 75], ["rare", 22], ["epic", 3], ["legendary", 0]],
  elite:  [["common", 35], ["rare", 45], ["epic", 17], ["legendary", 3]],
  boss:   [["common", 0],  ["rare", 55], ["epic", 35], ["legendary", 10]],
};

/**
 * Gewichtete Wahl mit EINEM vorgegebenen Wurf statt `Math.random()`.
 *
 * Gebraucht fuer alles, was auf Client und Server dasselbe ergeben muss —
 * seit dem 07.09.2026 traegt jeder Gegner eine feste Ausruestung, die aus
 * seinem Seed abgeleitet wird (`enemy-gear.ts`). Die Gewichte stehen weiter
 * nur hier; wer sie nachbaut, laesst sie auseinanderlaufen.
 */
export function pickWeightedWith<T>(
  entries: ReadonlyArray<readonly [T, number]>,
  wurf: number,
): T {
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = Math.min(0.999999, Math.max(0, wurf)) * total;
  for (const [v, w] of entries) {
    r -= w;
    if (r <= 0) return v;
  }
  return entries[entries.length - 1][0];
}

/** Seltenheit aus einem vorgegebenen Wurf — deterministische Schwester von `rollRarity`. */
export function rarityFromRoll(tier: "normal" | "elite" | "boss", wurf: number): Rarity {
  return pickWeightedWith(RARITY_WEIGHTS[tier], wurf);
}

/** Item-Level aus einem vorgegebenen Wurf — deterministische Schwester von `rollItemLevel`. */
export function itemLevelFromRoll(
  zone: number,
  tier: "normal" | "elite" | "boss",
  wurf: number,
): number {
  const off = pickWeightedWith(LEVEL_OFFSETS, wurf);
  return Math.max(1, zone + off + (tier === "boss" ? 1 : 0));
}

function pickWeighted<T>(entries: ReadonlyArray<readonly [T, number]>): T {
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = Math.random() * total;
  for (const [v, w] of entries) {
    r -= w;
    if (r <= 0) return v;
  }
  return entries[entries.length - 1][0];
}

export function rollRarity(tier: "normal" | "elite" | "boss"): Rarity {
  return pickWeighted(RARITY_WEIGHTS[tier]);
}

// Zonen-basiertes Level. Dreieckige Verteilung: Zone selten ±2, häufig nahe Mitte.
// Gewichte ungefähr 10/25/30/25/10 → Ränder selten.
const LEVEL_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-2, 10],
  [-1, 25],
  [ 0, 30],
  [+1, 25],
  [+2, 10],
];

export function rollItemLevel(
  zone: number,
  tier: "normal" | "elite" | "boss",
): number {
  const off = pickWeighted(LEVEL_OFFSETS);
  const tierBonus = tier === "boss" ? 1 : 0;
  return Math.max(1, zone + off + tierBonus);
}
